import { parseMultilineContent } from "@repo/app-types/content/plaintext";
import {
  LEARNING_JOURNEY_CONFIGURATION_AGENT_ID,
  type LearningJourneyDraftSnapshot,
  type LearningJourneyLessonDraft,
  type LearningJourneyOperation,
} from "@repo/app-types/entities/configurationAgent";
import {
  getChapterTitle,
  moveChapterAfter,
  moveChapterBefore,
  moveLessonAfter,
  moveLessonBefore,
  moveLessonToChapterEnd,
  type Chapter,
} from "@repo/app-types/entities/course";
import { getInterviewSectionTitle } from "@repo/app-types/entities/interviewSection/definition";
import type { InterviewSectionSettings } from "@repo/app-types/entities/interviewSection/settings";
import { InterviewSectionType } from "@repo/app-types/entities/interviewSection/types";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type {
  ProjectWithInterviewCount,
  ProjectWithJobType,
} from "@repo/app-types/entities/project";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import {
  buildProjectUrlPath,
  ProjectDetailsTabPath,
} from "@repo/app-types/helpers/adminAppRoutes";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Label } from "@repo/ui/Label/label";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useMemo, useState, type FC } from "react";
import { Tree, TreeItem, TreeItemContent } from "react-aria-components";
import { Link } from "react-router";
import { v4 as uuidv4 } from "uuid";
import {
  addActionItemContent,
  treeItem,
} from "../../../../components/tree/treeStyles.js";
import { Can } from "../../../../helpers/teammateAuthorizationContext.js";
import { LearningJourneyConfigurationAssistant } from "../../../agents/learningJourneyConfigurationAssistant/learningJourneyConfigurationAssistant.js";
import { MastraChatProvider } from "../../../agents/mastra/mastraChatContext.js";
import { useMastraChat } from "../../../agents/mastra/useMastraChat.js";
import { ChapterItemSettingsContent } from "../../../courses/chapterItemSettingsContent.js";
import { ChapterSettings } from "../../../courses/chapterSettings.js";
import { ChapterTreeItem } from "../../../courses/chapterTreeItem.js";
import { LessonItemSettingsContent } from "../../../courses/lessonItemSettingsContent.js";
import { LessonTreeItem } from "../../../courses/lessonTreeItem.js";
import { ChatQuizInterviewSectionDefinitionSettings } from "../../../interviewSections/definitions/chatQuiz/settings.js";
import { useInterviewSectionCreateFlow } from "../../../interviewSections/definitions/createFlow.js";
import { KnowledgeTestInterviewSectionDefinitionSettings } from "../../../interviewSections/definitions/knowledgeTest/settings.js";
import { RolePlayInterviewSectionDefinitionSettings } from "../../../interviewSections/definitions/rolePlay/settings.js";
import type { VisibleInterviewSectionDefinition } from "../../../interviewSections/definitions/state.js";
import { getInterviewSectionTypeDisplayValues } from "../../../interviewSections/types.js";
import {
  serializeTreeItemKey,
  useCourseTreeExpandAndCollapse,
} from "./courseTreeHelpers.js";
import {
  renderSectionPreviewTrigger,
  useCourseTreeDragAndDrop,
  useCourseTreeSelection,
} from "./courseTreeInteractions.js";
import {
  getLearningJourneyEntityNameById,
  requireLearningJourneyEntityId,
} from "./learningJourneyEntityIds.js";
import { useLearningJourneyEditor } from "./useLearningJourneyEditor.js";

interface LearningJourneyBuilderPageProps {
  project: ProjectWithInterviewCount & ProjectWithJobType;
}

export const LearningJourneyBuilderPage: FC<
  LearningJourneyBuilderPageProps
> = ({ project }) => {
  const editor = useLearningJourneyEditor(project);
  const {
    course,
    sections,
    sectionsById,
    playbookId,
    chapterChangeStatuses,
    lessonChangeStatuses,
    hasMadeChanges,
    isLoadingSections,
    isSaving,
    onCourseChange,
    onSectionCreate,
    onSectionUpdate,
    onSectionDelete,
    onDiscardChanges,
    onSaveChanges,
  } = editor;

  const draftSnapshot = useMemo<LearningJourneyDraftSnapshot | undefined>(
    () =>
      sections
        ? {
            playbookId,
            course,
            sections: sections.map((section) => ({
              id: section.id,
              settings: section.settings,
            })),
          }
        : undefined,
    [playbookId, course, sections],
  );

  const { expandedKeys, onExpandedChange } =
    useCourseTreeExpandAndCollapse(course);
  const { dragAndDropHooks } = useCourseTreeDragAndDrop(course, onCourseChange);
  const {
    selectedKeys,
    onSelectionChange,
    selectedChapter,
    selectedChapterNumber,
    selectedLessonNumber,
    selectedSection,
    setSelectedKey,
  } = useCourseTreeSelection({
    initialSelectedKey: undefined,
    course,
    sectionsById,
  });

  const onChapterCreateClick = () => {
    const newChapter: Chapter = {
      id: `client:${uuidv4()}`,
      lessons: [],
    };

    onCourseChange({
      ...course,
      chapters: [...course.chapters, newChapter],
    });
    setSelectedKey({ type: "chapter", id: newChapter.id });
  };

  const [lessonCreateRequest, setLessonCreateRequest] = useState<
    { chapterIndex: number } | undefined
  >(undefined);

  const onLessonCreateFinish = (settings: InterviewSectionSettings) => {
    if (!lessonCreateRequest) {
      return;
    }

    const newSectionId = onSectionCreate(settings);

    onCourseChange({
      ...course,
      chapters: course.chapters.map((chapter, index) => {
        if (index !== lessonCreateRequest.chapterIndex) {
          return chapter;
        }

        return {
          ...chapter,
          lessons: [
            ...chapter.lessons,
            { interviewSectionDefinitionId: newSectionId },
          ],
        };
      }),
    });

    setSelectedKey({ type: "lesson", sectionId: newSectionId });
  };

  const { onInterviewSectionCreateClick, renderInterviewSectionCreateFlow } =
    useInterviewSectionCreateFlow({
      isTrainingProject: true,
      title: "Add lesson",
      description: "Choose the type of lesson to add to your learning journey.",
      onSectionCreate: onLessonCreateFinish,
    });

  const onLessonCreateClick = (chapterIndex: number) => {
    setLessonCreateRequest({ chapterIndex });
    onInterviewSectionCreateClick();
  };

  const onDeleteClick = () => {
    if (selectedSection) {
      onSectionDelete(selectedSection.id);
      onCourseChange({
        ...course,
        chapters: course.chapters.map((chapter) => ({
          ...chapter,
          lessons: chapter.lessons.filter(
            (lesson) =>
              lesson.interviewSectionDefinitionId !== selectedSection.id,
          ),
        })),
      });
      setSelectedKey(undefined);
      return;
    }

    if (!selectedChapter) {
      return;
    }

    for (const lesson of selectedChapter.lessons) {
      onSectionDelete(lesson.interviewSectionDefinitionId);
    }

    onCourseChange({
      ...course,
      chapters: course.chapters.filter(
        (chapter) => chapter.id !== selectedChapter.id,
      ),
    });
    setSelectedKey(undefined);
  };

  const canConfigure = Boolean(playbookId);

  const handleSave = async () => {
    try {
      await onSaveChanges();
    } catch (error) {
      console.error("Failed to save learning journey", error);
    }
  };

  const treeStyle = `
    @scope {
      .react-aria-DropIndicator {
        outline: 1px solid #9333ea;
      }
    }
  `;

  const chapterNamesById = useMemo(
    () =>
      new Map(
        course.chapters.map((chapter, index) => [
          chapter.id,
          getChapterTitle(index, chapter),
        ]),
      ),
    [course.chapters],
  );

  const lessonNamesById = useMemo(
    () =>
      new Map(
        course.chapters.flatMap((chapter) =>
          chapter.lessons.map((lesson) => [
            lesson.interviewSectionDefinitionId,
            getInterviewSectionTitle(
              sectionsById?.get(lesson.interviewSectionDefinitionId)?.settings,
              "Untitled lesson",
            ),
          ]),
        ),
      ),
    [course.chapters, sectionsById],
  );

  const handleApplyOperation = async (operation: LearningJourneyOperation) => {
    const chapterIds = course.chapters.map((chapter) => chapter.id);
    const lessonIds = course.chapters.flatMap((chapter) =>
      chapter.lessons.map((lesson) => lesson.interviewSectionDefinitionId),
    );
    const requireChapterId = (id: string) =>
      requireLearningJourneyEntityId(chapterIds, id, "chapter");
    const requireLessonId = (id: string) =>
      requireLearningJourneyEntityId(lessonIds, id, "lesson");
    const requireLessonSection = (id: string) => {
      const lessonId = requireLessonId(id);
      const section = sectionsById?.get(lessonId);
      if (!section) {
        throw new Error(`Unable to find lesson ${id}`);
      }

      return {
        lessonId,
        section,
      };
    };

    switch (operation.type) {
      case "add-chapter": {
        const bundledLessons = operation.lessons ?? [];
        const newLessonIds = bundledLessons.map((lesson) =>
          onSectionCreate(buildLessonSettingsFromDraft(lesson)),
        );
        const newChapter: Chapter = {
          id: `client:${uuidv4()}`,
          topic: operation.topic,
          lessons: newLessonIds.map((lessonId) => ({
            interviewSectionDefinitionId: lessonId,
          })),
        };
        onCourseChange({
          ...course,
          chapters: [...course.chapters, newChapter],
        });
        setSelectedKey({ type: "chapter", id: newChapter.id });
        return;
      }
      case "update-chapter": {
        const chapterId = requireChapterId(operation.id);

        onCourseChange({
          ...course,
          chapters: course.chapters.map((chapter) =>
            chapter.id === chapterId
              ? {
                  ...chapter,
                  ...(operation.topic !== undefined
                    ? { topic: operation.topic }
                    : {}),
                }
              : chapter,
          ),
        });
        return;
      }
      case "remove-chapter": {
        const chapterId = requireChapterId(operation.id);

        const chapter = course.chapters.find((item) => item.id === chapterId);
        if (!chapter) {
          throw new Error(`Unable to find chapter ${operation.id}`);
        }

        for (const lesson of chapter.lessons) {
          onSectionDelete(lesson.interviewSectionDefinitionId);
        }

        onCourseChange({
          ...course,
          chapters: course.chapters.filter((item) => item.id !== chapterId),
        });
        setSelectedKey(undefined);
        return;
      }
      case "move-chapter": {
        const chapterId = requireChapterId(operation.id);
        const targetChapterId = requireChapterId(operation.targetChapterId);

        onCourseChange(
          operation.position === "before"
            ? moveChapterBefore(course, chapterId, targetChapterId)
            : moveChapterAfter(course, chapterId, targetChapterId),
        );
        return;
      }
      case "add-lesson": {
        const chapterId = requireChapterId(operation.chapterId);

        const newSectionId = onSectionCreate(
          buildLessonSettingsFromDraft(operation),
        );
        onCourseChange({
          ...course,
          chapters: course.chapters.map((chapter) =>
            chapter.id === chapterId
              ? {
                  ...chapter,
                  lessons: [
                    ...chapter.lessons,
                    { interviewSectionDefinitionId: newSectionId },
                  ],
                }
              : chapter,
          ),
        });
        setSelectedKey({ type: "lesson", sectionId: newSectionId });
        return;
      }
      case "update-lesson": {
        const { lessonId, section } = requireLessonSection(operation.id);

        onSectionUpdate(
          lessonId,
          updateLessonSettings(section.settings, operation),
        );
        setSelectedKey({ type: "lesson", sectionId: lessonId });
        return;
      }
      case "remove-lesson": {
        const lessonId = requireLessonId(operation.id);

        onSectionDelete(lessonId);
        onCourseChange({
          ...course,
          chapters: course.chapters.map((chapter) => ({
            ...chapter,
            lessons: chapter.lessons.filter(
              (lesson) => lesson.interviewSectionDefinitionId !== lessonId,
            ),
          })),
        });
        setSelectedKey(undefined);
        return;
      }
      case "move-lesson":
        if (operation.position === "end" && operation.targetChapterId) {
          const lessonId = requireLessonId(operation.id);
          const targetChapterId = requireChapterId(operation.targetChapterId);

          onCourseChange(
            moveLessonToChapterEnd(course, lessonId, targetChapterId),
          );
          return;
        }

        if (operation.position === "before" && operation.targetLessonId) {
          const lessonId = requireLessonId(operation.id);
          const targetLessonId = requireLessonId(operation.targetLessonId);

          onCourseChange(moveLessonBefore(course, lessonId, targetLessonId));
          return;
        }

        if (operation.position === "after" && operation.targetLessonId) {
          const lessonId = requireLessonId(operation.id);
          const targetLessonId = requireLessonId(operation.targetLessonId);

          onCourseChange(moveLessonAfter(course, lessonId, targetLessonId));
        }
        return;
      default:
        return;
    }
  };

  return (
    <div className="flex h-full min-w-[1100px] flex-col">
      <div className="flex items-center justify-between border-b border-border-light bg-background px-5 py-3">
        <div />

        <div className="flex items-center gap-2">
          {hasMadeChanges ? (
            <Button
              variant={ButtonVariant.Secondary}
              onClick={onDiscardChanges}
              isDisabled={isSaving}
            >
              Discard changes
            </Button>
          ) : null}

          <Can
            I={PermissionActions.UPDATE}
            a={PermissionSubjects.JOB_OPENINGS}
            passThrough={true}
          >
            {(allowed) => (
              <Button
                variant={ButtonVariant.Primary}
                onClick={() => void handleSave()}
                isDisabled={
                  !allowed || !hasMadeChanges || !canConfigure || isSaving
                }
                isLoading={isSaving}
              >
                Save changes
              </Button>
            )}
          </Can>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-white">
          {!canConfigure ? (
            <div className="flex h-full items-center justify-center px-5">
              <div className="max-w-md text-center">
                <h2 className="text-sm font-semibold text-foreground">
                  Select a playbook in the Overview tab
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Configuration uses the attached playbook and its knowledge
                  base to shape the learning journey.
                </p>
                <div className="mt-4">
                  <Link
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                    to={buildProjectUrlPath(
                      project.id,
                      ProjectDetailsTabPath.Overview,
                    )}
                  >
                    Go to Overview
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid h-full min-h-0 grid-cols-[400px_minmax(0,1fr)]">
              <div className="flex min-h-0 flex-col border-r border-border-light">
                <div className="flex items-center justify-between border-b border-border-light px-4 py-4">
                  <div>
                    <h2 className="text-sm font-semibold text-foreground">
                      Chapters and lessons
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      Drag to reorder chapters and lessons.
                    </p>
                  </div>
                  <Button
                    variant={ButtonVariant.Secondary}
                    onClick={onChapterCreateClick}
                  >
                    Add chapter
                  </Button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
                  <style hidden>{treeStyle}</style>
                  {isLoadingSections ? (
                    <div className="text-sm text-muted-foreground">
                      Loading lessons...
                    </div>
                  ) : (
                    <Tree
                      aria-label="Learning journey"
                      selectionMode="single"
                      selectionBehavior="replace"
                      selectedKeys={selectedKeys}
                      expandedKeys={expandedKeys}
                      onExpandedChange={onExpandedChange}
                      onSelectionChange={onSelectionChange}
                      dragAndDropHooks={dragAndDropHooks}
                      disabledBehavior="selection"
                    >
                      {course.chapters.map((chapter, chapterIndex) => (
                        <ChapterTreeItem
                          key={chapter.id}
                          chapter={chapter}
                          index={chapterIndex}
                        >
                          <TreeItemContent>
                            <ChapterItemSettingsContent
                              index={chapterIndex}
                              chapter={chapter}
                              changeStatus={chapterChangeStatuses.get(
                                chapter.id,
                              )}
                            />
                          </TreeItemContent>

                          {chapter.lessons.map((lesson) => {
                            const section = sectionsById?.get(
                              lesson.interviewSectionDefinitionId,
                            );

                            return (
                              <LessonTreeItem
                                key={lesson.interviewSectionDefinitionId}
                                sectionId={lesson.interviewSectionDefinitionId}
                                section={section}
                              >
                                <TreeItemContent>
                                  <LessonItemSettingsContent
                                    section={section}
                                    changeStatus={lessonChangeStatuses.get(
                                      lesson.interviewSectionDefinitionId,
                                    )}
                                  />
                                </TreeItemContent>
                              </LessonTreeItem>
                            );
                          })}

                          <TreeItem
                            key={`new-${chapter.id}`}
                            id={serializeTreeItemKey({
                              type: "new-lesson",
                              chapterId: chapter.id,
                            })}
                            textValue="Add lesson"
                            className={treeItem({
                              isLastItemAtLevel: true,
                            })}
                            onAction={() => onLessonCreateClick(chapterIndex)}
                            isDisabled
                          >
                            <TreeItemContent>
                              <div className={addActionItemContent()}>
                                <IconPlus className="size-4" />
                                Add lesson
                              </div>
                            </TreeItemContent>
                          </TreeItem>
                        </ChapterTreeItem>
                      ))}
                    </Tree>
                  )}

                  {renderInterviewSectionCreateFlow()}
                </div>
              </div>

              <div className="flex min-h-0 flex-col">
                <div className="flex items-center justify-between border-b border-border-light px-4 py-4">
                  <div>
                    <h2 className="text-sm font-semibold text-foreground">
                      {selectedSection
                        ? `Lesson ${selectedLessonNumber}: ${
                            getInterviewSectionTypeDisplayValues(
                              selectedSection.settings.type,
                            ).title
                          }`
                        : selectedChapter
                          ? `Chapter ${selectedChapterNumber}`
                          : "Select a chapter or lesson"}
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      {selectedSection
                        ? "Edit lesson content and instructions."
                        : selectedChapter
                          ? "Refine the chapter focus and grading context."
                          : "Choose an item from the left to edit it here."}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {selectedSection
                      ? renderSectionPreviewTrigger(project.id, selectedSection)
                      : null}

                    {selectedSection || selectedChapter ? (
                      <Button
                        variant={ButtonVariant.Warning}
                        icon={<IconTrash className="size-4" />}
                        onClick={onDeleteClick}
                      >
                        Delete
                      </Button>
                    ) : null}
                  </div>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto">
                  {selectedSection ? (
                    <>
                      <div className="border-b border-gray-100 px-4 py-3 text-sm text-muted-foreground">
                        <div className="max-w-[760px]">
                          {
                            getInterviewSectionTypeDisplayValues(
                              selectedSection.settings.type,
                            ).description
                          }
                        </div>
                      </div>
                      <div className="w-full">
                        {renderSectionSettings({
                          section: selectedSection,
                          onSectionUpdate,
                        })}
                      </div>
                    </>
                  ) : null}

                  {selectedChapter ? (
                    <div className="max-w-[760px]">
                      <ChapterSettings
                        chapter={selectedChapter}
                        playbookId={playbookId}
                        onChange={(chapterId, patchChapter) => {
                          onCourseChange({
                            ...course,
                            chapters: course.chapters.map((existingChapter) => {
                              if (existingChapter.id === chapterId) {
                                return patchChapter(existingChapter);
                              }
                              return existingChapter;
                            }),
                          });
                        }}
                      />
                    </div>
                  ) : null}

                  {!selectedSection && !selectedChapter ? (
                    <div className="flex h-full min-h-[240px] items-center justify-center px-5 text-center text-sm text-muted-foreground">
                      Select a chapter or lesson from the left to edit it.
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex h-full w-[400px] shrink-0 flex-col border-l border-border-light bg-background">
          <div className="border-b border-border-light bg-white px-4 py-3">
            <Label size={Size.SMALL}>AI Assistant</Label>
          </div>
          <div className="flex min-h-0 flex-1 flex-col">
            {canConfigure ? (
              <LearningJourneyAssistantPanel
                project={project}
                playbookId={playbookId}
                draftSnapshot={draftSnapshot}
                onApplyOperation={handleApplyOperation}
                getEntityName={(id, type) => {
                  if (type === "chapter") {
                    return getLearningJourneyEntityNameById(
                      chapterNamesById,
                      id,
                    );
                  }

                  return getLearningJourneyEntityNameById(lessonNamesById, id);
                }}
              />
            ) : (
              <div className="flex flex-1 items-center justify-center px-5 text-center text-sm text-muted-foreground">
                Select a playbook in Overview to use the assistant.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface LearningJourneyAssistantPanelProps {
  project: ProjectWithInterviewCount & ProjectWithJobType;
  playbookId: PlaybookId | null;
  draftSnapshot: LearningJourneyDraftSnapshot | undefined;
  onApplyOperation: (operation: LearningJourneyOperation) => Promise<void>;
  getEntityName: (id: string, type: "chapter" | "lesson") => string | undefined;
}

/**
 * Owns the assistant chat so streaming updates re-render only this panel.
 * Hoisting `useMastraChat` into the page would re-render the whole builder
 * (tree, drag-and-drop, lesson editors) on every streamed chunk.
 */
const LearningJourneyAssistantPanel: FC<LearningJourneyAssistantPanelProps> = ({
  project,
  playbookId,
  draftSnapshot,
  onApplyOperation,
  getEntityName,
}) => {
  const chat = useMastraChat({
    agentId: LEARNING_JOURNEY_CONFIGURATION_AGENT_ID,
    onSend: async () => ({
      requestContext: {
        projectId: project.id,
        jobTypeId: project.job_type.id,
        playbookId,
        draftLearningJourney: draftSnapshot,
      },
    }),
  });

  return (
    <MastraChatProvider chat={chat}>
      <LearningJourneyConfigurationAssistant
        onApplyOperation={onApplyOperation}
        getEntityName={getEntityName}
      />
    </MastraChatProvider>
  );
};

function renderSectionSettings(args: {
  section: VisibleInterviewSectionDefinition;
  onSectionUpdate: (id: string, settings: InterviewSectionSettings) => void;
}) {
  const { section, onSectionUpdate } = args;
  const settings = section.settings;

  switch (settings.type) {
    case InterviewSectionType.KnowledgeTest:
      return (
        <div className="space-y-4">
          <div className="px-4 pt-4 pb-0">
            <Input
              label="Topic"
              value={settings.title ?? ""}
              onChange={(event) => {
                onSectionUpdate(section.id, {
                  ...settings,
                  title: event.target.value,
                });
              }}
              maxLength={100}
            />
          </div>

          <KnowledgeTestInterviewSectionDefinitionSettings
            key={section.id}
            interviewSectionDefinitionId={section.id}
            settings={settings}
            onChange={onSectionUpdate}
          />
        </div>
      );
    case InterviewSectionType.RolePlay:
      return (
        <div className="space-y-4">
          <div className="px-4 pt-4 pb-0">
            <Input
              label="Topic"
              description="This will be visible to participants to help them understand what they will learn."
              placeholder="Example: Handling price objections"
              value={settings.title ?? ""}
              onChange={(event) => {
                onSectionUpdate(section.id, {
                  ...settings,
                  title: event.target.value,
                });
              }}
              maxLength={100}
            />
          </div>

          <RolePlayInterviewSectionDefinitionSettings
            key={section.id}
            interviewSectionDefinitionId={section.id}
            settings={settings}
            onChange={onSectionUpdate}
          />
        </div>
      );
    case InterviewSectionType.ChatQuiz:
      return (
        <div className="space-y-4">
          <div className="px-4 pt-4 pb-0">
            <Input
              label="Topic"
              description="This will be visible to participants to help them understand what they will learn."
              placeholder="Example: Pricing and discounts"
              value={settings.topic ?? ""}
              onChange={(event) => {
                onSectionUpdate(section.id, {
                  ...settings,
                  topic: event.target.value,
                });
              }}
              maxLength={100}
            />
          </div>

          <ChatQuizInterviewSectionDefinitionSettings
            key={section.id}
            interviewSectionDefinitionId={section.id}
            settings={settings}
            onChange={onSectionUpdate}
          />
        </div>
      );
    case InterviewSectionType.Questions:
      return (
        <div className="p-4 text-sm text-muted-foreground">
          This legacy lesson type is not supported in the Journey Builder.
        </div>
      );
    default:
      return assertUnreachable(settings, () => null);
  }
}

function buildLessonSettingsFromDraft(
  operation: LearningJourneyLessonDraft,
): InterviewSectionSettings {
  switch (operation.lessonType) {
    case "chat-quiz":
      return {
        type: InterviewSectionType.ChatQuiz,
        topic: operation.topic ?? operation.title ?? "New chat quiz",
        questions: (operation.questions ?? []).map((question: string) => ({
          question,
        })),
      };
    case "knowledge-test":
      return {
        type: InterviewSectionType.KnowledgeTest,
        title: operation.title ?? operation.topic ?? "New knowledge test",
        questions: (operation.questions ?? []).map((question: string) => ({
          question,
        })),
      };
    case "role-play":
      return {
        type: InterviewSectionType.RolePlay,
        title: operation.title ?? operation.topic ?? "New role play",
        participant_instructions: parseMultilineContent(
          operation.participantInstructions ??
            `Situation:\nAdd the learner-facing scenario context.\n\nObjective:\nDescribe what the learner should accomplish at a high level.`,
          { parsesVariables: false },
        ),
        agent_instructions: parseMultilineContent(
          operation.agentInstructions ??
            `Identity and background:\nDescribe who the AI should play.\n\nScenario:\nDefine the backdrop of the conversation, why it is happening, and what the AI already knows.\n\nResponse and speaking style:\nDescribe tone, pacing, and verbosity.\n\nOpening line:\nWrite the exact first line the AI should say.\n\nWhat to volunteer vs what to hold back:\nList what the agent should share early/openly versus only when asked.\n\nHow to respond by topic:\nProvide the general responses the AI should give for each major topic.\n\nConversation milestones and challenges:\nList the moments the learner should have to navigate and the structure of the conversation.\n\nEnding milestones:\nDescribe when and how the role play should end.`,

          { parsesVariables: false },
        ),
      };
    default:
      throw new Error("Unsupported lesson type");
  }
}

function updateLessonSettings(
  currentSettings: InterviewSectionSettings,
  operation: Extract<LearningJourneyOperation, { type: "update-lesson" }>,
): InterviewSectionSettings {
  switch (currentSettings.type) {
    case InterviewSectionType.ChatQuiz:
      return {
        ...currentSettings,
        ...(operation.topic !== undefined ? { topic: operation.topic } : {}),
        ...(operation.title !== undefined ? { topic: operation.title } : {}),
        ...(operation.questions
          ? {
              questions: operation.questions.map((question) => ({ question })),
            }
          : {}),
      };
    case InterviewSectionType.KnowledgeTest:
      return {
        ...currentSettings,
        ...(operation.topic !== undefined ? { title: operation.topic } : {}),
        ...(operation.title !== undefined ? { title: operation.title } : {}),
        ...(operation.questions
          ? {
              questions: operation.questions.map((question) => ({ question })),
            }
          : {}),
      };
    case InterviewSectionType.RolePlay:
      return {
        ...currentSettings,
        ...(operation.title !== undefined ? { title: operation.title } : {}),
        ...(operation.topic !== undefined ? { title: operation.topic } : {}),
        ...(operation.participantInstructions
          ? {
              participant_instructions: parseMultilineContent(
                operation.participantInstructions,
                { parsesVariables: false },
              ),
            }
          : {}),
        ...(operation.agentInstructions
          ? {
              agent_instructions: parseMultilineContent(
                operation.agentInstructions,
                { parsesVariables: false },
              ),
            }
          : {}),
      };
    case InterviewSectionType.Questions:
      return currentSettings;
    default:
      return currentSettings;
  }
}
